<template>
  <div v-if="ready" class="feature">
    <FeatureMeta :feature="featureStore.feature" />

    <div v-if="featureStore.images.length === 0">
      <FeatureInitSection include-uploader :feature="featureStore.feature" />
      <PageResizeImage />
    </div>

    <div v-else class="h-full">
      <LayoutDefault @finished="onFinished">
        <template #aside>
          <div class="w-full flex border border-gray-300 dark:border-gray-600 text-normal">
            <button
              @click="featureStore.options[slug].resizeType = 'pixel'"
              :class="
                featureStore.options[slug].resizeType === 'pixel'
                  ? 'bg-blue-400 text-white dark:bg-blue-900 dark:text-gray-200'
                  : ''
              "
              class="p-3 w-1/2 flex lg:flex-col justify-center items-center text-2xl lg:text-5xl"
            >
              <i class="fa-solid fa-chess-board"></i>
              <span class="text-base lg:text-lg">By pixel</span>
            </button>
            <button
              @click="featureStore.options[slug].resizeType = 'percentage'"
              :class="
                featureStore.options[slug].resizeType === 'percentage'
                  ? 'bg-blue-400 text-white dark:bg-blue-900 dark:text-gray-200'
                  : ''
              "
              class="p-3 w-1/2 flex lg:flex-col justify-center items-center text-2xl lg:text-5xl"
            >
              <i class="fa-solid fa-percent"></i>
              <span class="text-base lg:text-lg">By percentage</span>
            </button>
          </div>

          <div class="mt-8">
            <div v-if="featureStore.options[slug].resizeType === 'pixel'" class="flex flex-col space-y-6">
              <div class="flex items-center justify-between">
                <label for="resizeWidth" class="input-label">Resize width (px)</label>
                <input
                  id="resizeWidth"
                  v-model="featureStore.options[slug].resizeWidth"
                  min="1"
                  max="8000"
                  type="number"
                  class="input-field w-32 text-right"
                  placeholder="1024"
                  @input="debounceCalculateByWidth"
                />
              </div>
              <div class="flex items-center justify-between">
                <label for="resizeHeight" class="input-label">Resize height (px)</label>
                <input
                  id="resizeHeight"
                  v-model="featureStore.options[slug].resizeHeight"
                  min="1"
                  max="8000"
                  type="number"
                  class="input-field w-32 text-right"
                  placeholder="512"
                  @input="debounceCalculateByHeight"
                />
              </div>
              <div class="ml-1 flex items-center">
                <input
                  id="keep-ratio-checkbox"
                  type="checkbox"
                  v-model="featureStore.options[slug].keepRatio"
                  class="w-6 h-6 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
                />
                <label for="keep-ratio-checkbox" class="ms-2 text-base font-medium text-gray-900 dark:text-gray-300"
                  >Keep ratio</label
                >
              </div>
            </div>

            <ul
              v-else
              class="w-full flex lg:flex-col text-sm lg:text-base font-medium text-gray-900 bg-white border border-gray-200 rounded-lg dark:bg-gray-700 dark:border-gray-600 dark:text-white"
            >
              <li
                v-for="percentage in [25, 50, 75]"
                :key="percentage"
                class="w-full border-b border-gray-200 rounded-t-lg dark:border-gray-600"
              >
                <div class="flex items-center ps-3">
                  <input
                    :id="`percentage_${percentage}`"
                    type="radio"
                    v-model="featureStore.options[slug].resizePercentage"
                    :value="percentage"
                    name="list-radio"
                    class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-700 dark:focus:ring-offset-gray-700 focus:ring-2 dark:bg-gray-600 dark:border-gray-500"
                  />
                  <label
                    :for="`percentage_${percentage}`"
                    class="w-full py-3 ms-2 font-medium text-gray-900 dark:text-gray-300"
                    >{{ percentage }}%<span class="hidden lg:inline"> smaller</span></label
                  >
                </div>
              </li>
            </ul>
          </div>
        </template>
      </LayoutDefault>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { until } from '@vueuse/core'
  import { watch } from 'vue'
  import { useFeatureSetup } from '$/composables/useFeatureSetup'
  import PageResizeImage from '&/landing_pages/PageResizeImage.vue'
  import FeatureMeta from '&/FeatureMeta.vue'

  const { ready, router, featureStore, slug, LayoutDefault, FeatureInitSection } = useFeatureSetup()

  function onFinished() {
    router.push({ name: 'ResizeImageResult' })
  }

  // Delay 1 second and calculate result dimensions
  let debounceTimer = null

  function debounceCalculateByWidth() {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
      if (featureStore.options[slug].keepRatio) {
        featureStore.options[slug].resizeHeight = null
        document.getElementById('resizeHeight').placeholder = 'Auto'
      }
      calculateResultDimensions()
    }, 1000)
  }

  function debounceCalculateByHeight() {
    clearTimeout(debounceTimer)
    debounceTimer = setTimeout(() => {
      if (featureStore.options[slug].keepRatio) {
        featureStore.options[slug].resizeWidth = null
        document.getElementById('resizeWidth').placeholder = 'Auto'
      }
      calculateResultDimensions('height')
    }, 1000)
  }

  function calculateResultDimensions(priority = 'width') {
    featureStore.imageData.forEach((image) => {
      if (featureStore.options[slug].resizeType === 'pixel') {
        const ratio = image.width / image.height
        if (priority === 'width') {
          image.resizeWidth = featureStore.options[slug].resizeWidth
          if (featureStore.options[slug].keepRatio) {
            image.resizeHeight = Math.round(featureStore.options[slug].resizeWidth / ratio)
          } else {
            image.resizeHeight = featureStore.options[slug].resizeHeight
          }
        } else {
          image.resizeHeight = featureStore.options[slug].resizeHeight
          if (featureStore.options[slug].keepRatio) {
            image.resizeWidth = Math.round(featureStore.options[slug].resizeHeight * ratio)
          } else {
            image.resizeWidth = featureStore.options[slug].resizeWidth
          }
        }
      } else {
        image.resizeWidth = Math.round((image.width * featureStore.options[slug].resizePercentage) / 100)
        image.resizeHeight = Math.round((image.height * featureStore.options[slug].resizePercentage) / 100)
      }
    })
  }

  watch(
    () => [
      featureStore.options[slug].resizeType,
      featureStore.options[slug].keepRatio,
      featureStore.options[slug].resizePercentage,
    ],
    () => {
      calculateResultDimensions()
    },
    { immediate: true }
  )

  watch(
    () => featureStore.images,
    async (newVal) => {
      if (
        newVal &&
        newVal.length > 0 &&
        !featureStore.options[slug].resizeWidth &&
        !featureStore.options[slug].resizeHeight
      ) {
        await until(() => featureStore.imageData[0]?.width).toBeTruthy()
        featureStore.options[slug].resizeWidth = Math.round(featureStore.imageData[0].width / 2)
        featureStore.options[slug].resizeHeight = Math.round(featureStore.imageData[0].height / 2)
      }
      setTimeout(() => {
        calculateResultDimensions()
      }, 500)
    },
    { immediate: true, deep: true }
  )
</script>
